import type { IFilter, ListingsAPIRequestBody } from '@noths/polaris-client-listings-api';
import {
  ListingsRequestAndFilterBuilder,
  ListingsRequestBuilder,
  ListingsRequestFacetBuilder,
  ListingsRequestFacetIntervalBuilder,
  ListingsRequestOrFilterBuilder,
  ListingsRequestRangeFilterBuilder,
  ListingsRequestTextFilterBuilder,
} from '@noths/polaris-client-listings-api';
import { deliveryZoneCodes } from '@noths/polaris-client-localisation';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectPageCategories } from 'src/redux/productsQuery/selectors/selectPageCategories';
import { GUARANTEED_ARRIVAL_DAYS } from 'src/utils/guaranteedArrivalDays';

export const PAGE_SIZE = 60;

const buildRangeFilter = (facetKey: string, value: string): ListingsRequestRangeFilterBuilder => {
  const [min, max] = value.split('-').map(Number);
  const filter = new ListingsRequestRangeFilterBuilder(facetKey);

  if (!isNaN(min)) {
    filter.min(min);
  }
  if (!isNaN(max) && max !== 0) {
    filter.max(max);
  }

  return filter;
};

export const adaptStoreToRequestBody = (state: ReduxApplicationState): ListingsAPIRequestBody => {
  const { products, productsQuery, user } = state;
  const page = productsQuery.page ?? 1;
  /* A batch that carried sponsored products ends its organic results short of a
     full page, so the next batch starts where it stopped rather than at the page
     arithmetic. Without sponsored products no cursor is stored and the offset is
     exactly what it always was. */
  const organicCursor = products?.organicCursors?.[page - 1];
  const offset = organicCursor ?? PAGE_SIZE * (page - 1);
  /* The zone scopes the `delivery` option the API resolves per product, so it is
     sent on every request rather than applied to the response. Omitting it is not
     neutral: the API reads an absent zone as mainland UK and would quote a
     non-UK customer a UK price.

     The store can legitimately hold no zone: a visitor arriving with no locale
     cookie has none to seed. This is the single place that decides what an
     unknown zone means, which is why the pages pass theirs through undefined
     rather than each picking a default. Mainland UK matches the same choice
     `resolveRequestContext` makes for the preview endpoint. */
  const listingsRequestBody = new ListingsRequestBuilder()
    .currency(user.currencyCode)
    .deliveryZone(user.deliveryZone ?? deliveryZoneCodes[1])
    .pageSize(PAGE_SIZE)
    .offset(offset);

  if (productsQuery.guaranteedArrivalDay) {
    GUARANTEED_ARRIVAL_DAYS[productsQuery.guaranteedArrivalDay].applyToRequest(listingsRequestBody);
  }

  if (productsQuery.searchTerm) {
    listingsRequestBody.searchTerm(productsQuery.searchTerm);
  }

  const pageCategories = selectPageCategories(state);
  if (pageCategories) {
    listingsRequestBody.pageCategories(pageCategories);
  }

  const facetsWithActiveOptions = (productsQuery.facets || []).filter((facet) => {
    return facet.options.some((option) => option.active);
  });

  const rootFilters: IFilter[] = [];

  if (productsQuery.canonicalFilters) {
    if ('and' in productsQuery.canonicalFilters) {
      productsQuery.canonicalFilters.and.forEach((filter) => {
        if ('text' in filter) {
          const textFilter = new ListingsRequestTextFilterBuilder(filter.key, filter.text);
          rootFilters.push(textFilter.build());
        }
      });
    }
  }

  if (productsQuery.facets) {
    const taxonomyAliases = productsQuery.taxonomyAttributeAliases || [];
    const facetsToInclude = productsQuery.facets
      .filter((facet) => facet.static || taxonomyAliases.includes(facet.alias))
      .filter((facet) => {
        return !(facet.valueType === 'numeric' && facet.options.length === 0);
      });

    const facetSpecs = facetsToInclude.map((facet) => {
      const facetBuilder = new ListingsRequestFacetBuilder(facet.key);

      if (facet.valueType === 'numeric') {
        const intervals = facet.options.map((option) => {
          const [min, max] = option.value.split('-').map(Number);
          const intervalBuilder = new ListingsRequestFacetIntervalBuilder();

          if (!isNaN(min)) {
            intervalBuilder.min(min);
          }
          if (!isNaN(max) && max !== 0) {
            intervalBuilder.max(max);
          }

          return intervalBuilder.build();
        });
        facetBuilder.intervals(intervals);
      }

      facetBuilder.excludedFilterKeys(facet.key);

      return facetBuilder.build();
    });

    listingsRequestBody.facetSpecs(facetSpecs);

    if (facetsWithActiveOptions.length > 0) {
      facetsWithActiveOptions.forEach((facet) => {
        if (facet.valueType === 'numeric') {
          const activeOptions = facet.options.filter((option) => option.active);

          if (activeOptions.length > 1) {
            const orFilter = new ListingsRequestOrFilterBuilder();
            const rangeFilters = activeOptions.map((option) => {
              return buildRangeFilter(facet.key, option.value).build();
            });
            orFilter.filters(rangeFilters);

            rootFilters.push(orFilter.build());
          } else {
            const rangeFilter = buildRangeFilter(facet.key, activeOptions[0].value);

            rootFilters.push(rangeFilter.build());
          }
        } else {
          const textFilter = new ListingsRequestTextFilterBuilder(
            facet.key,
            facet.options.filter((option) => option.active).map((option) => String(option.value)),
          );

          rootFilters.push(textFilter.build());
        }
      });
    }
  }

  if (rootFilters.length > 0) {
    const rootFilter = new ListingsRequestAndFilterBuilder().filters(rootFilters);
    listingsRequestBody.filter(rootFilter.build());
  }

  const activeSortOption = (productsQuery.sortOptions || []).find((option) => option.active);

  if (activeSortOption) {
    if (activeSortOption.key) {
      listingsRequestBody.sortBy(activeSortOption.key, activeSortOption.descending);
    }
  }

  return listingsRequestBody.build();
};
